/**
 * sw-sitemap-register.js
 *
 * Registers the sitemap service worker and exposes a helper
 * to clear cached entries for a specific facility.
 *
 * Also exports the opt-in cache header used by FacilityScene.vue so
 * that only requests from that component are intercepted by the
 * service worker. The raw service worker file
 * cannot import these constants, so the same literal values are
 * duplicated in src/sw-sitemap.js — keep them in sync.
 */

const SW_PATH = '/sw-sitemap.js';

export const SITEMAP_CACHE_HEADER = 'X-RS-Cache';
export const SITEMAP_CACHE_VALUE = 'sitemap';

// Must match CACHE_NAME in src/sw-sitemap.js — keep in sync.
const SITEMAP_CACHE_NAME = 'sitemap-cache-v1';

let registrationPromise = null;

export function registerSitemapSW() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    console.warn('[sw-sitemap] Service workers not supported');
    return Promise.resolve(null);
  }

  if (registrationPromise) {
    return registrationPromise;
  }

  registrationPromise = navigator.serviceWorker.register(SW_PATH, { scope: '/' }).then((reg) => {
    console.log('[sw-sitemap] Registered, scope:', reg.scope);
    return reg;
  }).catch((err) => {
    registrationPromise = null;
    console.error('[sw-sitemap] Registration failed:', err);
    return null;
  });

  return registrationPromise;
}

/**
 * Tell the service worker to delete all cache entries related to
 * the given facilityId.
 */
export function clearFacilityCache(facilityId) {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  function postToWorker(sw) {
    if (sw) {
      sw.postMessage({
        type: 'CLEAR_FACILITY_CACHE',
        facilityId,
      });
      console.log('[sw-sitemap] Sent CLEAR_FACILITY_CACHE for:', facilityId);
    }
  }

  // Prefer the active controller if already available
  if (navigator.serviceWorker.controller) {
    postToWorker(navigator.serviceWorker.controller);
    return;
  }

  // Fall back to waiting for the first registration to activate
  navigator.serviceWorker.ready.then((reg) => {
    postToWorker(reg.active);
  });
}

/**
 * Wipe the entire sitemap cache. Used on logout so that cached private
 * API responses (facility/unit/customer data) from one auth context are
 * never served to a different user who later logs in on the same browser.
 *
 * Operates directly on the Cache Storage API from the page so the deletion
 * happens synchronously enough to complete before the logout redirect
 * navigates away, rather than relying on a postMessage round-trip.
 */
export function clearAllSitemapCache() {
  if (typeof window === 'undefined' || !('caches' in window)) return;
  window.caches.delete(SITEMAP_CACHE_NAME).then((deleted) => {
    console.log('[sw-sitemap] Cleared all sitemap cache:', deleted);
  }).catch((err) => {
    console.warn('[sw-sitemap] Failed to clear all sitemap cache:', err);
  });
}



// WEBPACK FOOTER //
// ./src/sw-sitemap-register.js